import { z } from "zod";
import {
  PlayerGameViewSchema,
  RoomCredentialSchema,
  type Card,
  type PlayerGameView,
  type RoomCredential,
} from "../game/types";

const RoomEntrySchema = z.object({
  credential: RoomCredentialSchema,
  state: PlayerGameViewSchema,
});

const StateEnvelopeSchema = z.object({
  state: PlayerGameViewSchema,
});

const ErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});

const SocketEnvelopeSchema = z.object({
  type: z.literal("state"),
  state: PlayerGameViewSchema,
});

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

function credentialHeaders(credential: RoomCredential): HeadersInit {
  return {
    Authorization: `Bearer ${credential.token}`,
    "X-Player-Id": credential.playerId,
  };
}

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(path, init);
  const json: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = ErrorEnvelopeSchema.safeParse(json);
    throw new ApiError(
      parsed.success ? parsed.data.error.code : "REQUEST_FAILED",
      parsed.success
        ? parsed.data.error.message
        : "The table service did not return a usable response.",
      response.status,
    );
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(
      "INVALID_RESPONSE",
      "The table service returned an unexpected response. Refresh and try again.",
      502,
    );
  }
  return parsed.data;
}

function jsonRequest(
  method: "POST",
  body: unknown,
  credential?: RoomCredential,
): RequestInit {
  return {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(credential ? credentialHeaders(credential) : {}),
    },
    body: JSON.stringify(body),
  };
}

export async function createRoom(name: string) {
  return request(
    "/api/rooms",
    RoomEntrySchema,
    jsonRequest("POST", { name }),
  );
}

export async function joinRoom(roomCode: string, name: string) {
  return request(
    `/api/rooms/${encodeURIComponent(roomCode)}/join`,
    RoomEntrySchema,
    jsonRequest("POST", { name }),
  );
}

export async function getRoom(credential: RoomCredential) {
  const result = await request(
    `/api/rooms/${encodeURIComponent(credential.roomCode)}`,
    StateEnvelopeSchema,
    { headers: credentialHeaders(credential) },
  );
  return result.state;
}

async function mutateRoom(
  credential: RoomCredential,
  action: string,
  body: unknown = {},
): Promise<PlayerGameView> {
  const result = await request(
    `/api/rooms/${encodeURIComponent(credential.roomCode)}/${action}`,
    StateEnvelopeSchema,
    jsonRequest("POST", body, credential),
  );
  return result.state;
}

export const startRoom = (credential: RoomCredential) =>
  mutateRoom(credential, "start");

export const restartRoom = (credential: RoomCredential) =>
  mutateRoom(credential, "restart");

export const playRoomCards = (
  credential: RoomCredential,
  cards: Card[],
) => mutateRoom(credential, "play", { cards });

export const passRoomTurn = (credential: RoomCredential) =>
  mutateRoom(credential, "pass");

export const kickRoomPlayer = (
  credential: RoomCredential,
  playerId: string,
) => mutateRoom(credential, "kick", { playerId });

export async function leaveRoom(credential: RoomCredential): Promise<void> {
  await request(
    `/api/rooms/${encodeURIComponent(credential.roomCode)}/leave`,
    z.object({ left: z.literal(true) }),
    jsonRequest("POST", {}, credential),
  );
}

export function connectRoomSocket(
  credential: RoomCredential,
  callbacks: {
    onState: (state: PlayerGameView) => void;
    onOpen: () => void;
    onClose: (event: CloseEvent) => void;
  },
): WebSocket {
  const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
  const socket = new WebSocket(
    `${scheme}//${window.location.host}/api/rooms/${encodeURIComponent(
      credential.roomCode,
    )}/socket`,
    [
      "pusoy-dos",
      `player.${credential.playerId}`,
      `token.${credential.token}`,
    ],
  );

  socket.addEventListener("open", callbacks.onOpen);
  socket.addEventListener("close", callbacks.onClose);
  socket.addEventListener("message", (event) => {
    if (typeof event.data !== "string") return;
    let json: unknown;
    try {
      json = JSON.parse(event.data);
    } catch {
      return;
    }
    const parsed = SocketEnvelopeSchema.safeParse(json);
    if (parsed.success) callbacks.onState(parsed.data.state);
  });

  return socket;
}
